import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Stack
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import React, {useRef} from "react";
import {useTranslation} from "react-i18next";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {useCheckLinkedResources} from "crqlar-react-common/dist/utils/reservation.util";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useAvailability} from "crqlar-react-common/dist/utils/hooks/availability/useResourceAvailability";
import {isReservationInAvailability} from "crqlar-react-common/dist/utils/resource.util";

interface Props {
    open: boolean
    onClose: () => void,
    onConfirm: (notify: boolean) => Promise<void> | void
    reservation?: Reservation
    originalReservation?: Reservation
}

const ConfirmReservationUpdateDialog = (props: Props) => {

    const {t} = useTranslation()
    const [confirming, setConfirming] = React.useState(false)
    const [saveMenuAnchor, setSaveMenuAnchor] = React.useState<null | HTMLElement>(null)
    const saveButtonGroupRef = useRef<HTMLDivElement>(null)

    const resourceWarnings = useCheckLinkedResources(props.reservation)

    // This are the target resources where the user dragged & dropped the reservation slot
    const resources = useResources(props.reservation?.resourceIds || [])
    // console.log('res', resources)

    // We get only the availability periods for the target resource
    const availabilityQ = useAvailability(resources.map(res => res.id as string) || [])
    const availabilityData = availabilityQ.data || []

    // console.log('availabilityData', availabilityData)

    const isReservationTimeDifferent = (): boolean => {
        if (!props.reservation?.expectedStartDate || !props.originalReservation?.expectedStartDate ||
            !props.reservation.expectedEndDate || !props.originalReservation.expectedEndDate) {
            return false;
        }

        return props.reservation?.expectedStartDate.getTime() !== props.originalReservation?.expectedStartDate.getTime() ||
            props.reservation.expectedEndDate.getTime() !== props.originalReservation.expectedEndDate.getTime();
    }

    const renderResourceWarnings = () => {
        if (!resourceWarnings.notLinkedResources.length) {
            return null
        }

        return (
            <Box>
                <Alert severity={'warning'}>
                    {resourceWarnings.message}
                </Alert>
            </Box>
        )
    }

    const renderUnavailableResourceWarning = () => {
        // We hand over only the non-child resources to the availability check -> as the child resources do not have availability
        const nonChildResources = resources.filter(r => !r.parentId)
        if(isReservationInAvailability(nonChildResources, availabilityData, props.reservation)) return null

        return (
            <Box>
                <Alert
                    severity="warning"
                    sx={{
                        padding: "0px 12px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        fontSize: "0.9rem"
                    }}
                >
                    {t('Resources are not available for the selected time!')}
                </Alert>
            </Box>
        )
    }

    const renderDifferentTimeWarning = () => {
        if (isReservationTimeDifferent()) {
            return (
                <Box>
                    <Alert
                        severity="warning"
                        sx={{
                            padding: "0px 12px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            fontSize: "0.9rem"
                        }}
                    >
                        {t('Reservation time changed!')}
                    </Alert>
                </Box>
            )
        }
    }

    // sendEmail carries the staff's explicit intent: the split-button main action passes
    // false (silent save), the "& Send Email" menu item passes true — mirroring the save
    // options in the reservation dialog (ReservationDetailsWrapper).
    const handleConfirm = async (sendEmail: boolean) => {
        if (confirming) {
            return
        }

        setConfirming(true)

        try {
            await props.onConfirm(sendEmail)
        } finally {
            setConfirming(false)
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={confirming ? undefined : props.onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {t("Are you sure?")}
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    {renderResourceWarnings()}
                    {renderDifferentTimeWarning()}
                    {renderUnavailableResourceWarning()}
                </Stack>

            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={confirming}>{t('Close')}</Button>
                {/* Split "Save ▾" — main action saves silently, the menu item saves and
                    sends the update transactional email, mirroring the save options in
                    the reservation dialog. */}
                <ButtonGroup ref={saveButtonGroupRef} variant="contained" color="secondary">
                    <LoadingButton
                        color={'secondary'}
                        variant={'contained'}
                        onClick={() => handleConfirm(false)}
                        loading={confirming}
                    >
                        {t('Save')}
                    </LoadingButton>
                    <Button
                        color={'secondary'}
                        variant={'contained'}
                        size="small"
                        sx={{ px: 0.5, minWidth: 'auto' }}
                        disabled={confirming}
                        onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
                    >
                        <ArrowDropDownIcon/>
                    </Button>
                </ButtonGroup>
                <Menu
                    anchorEl={saveButtonGroupRef.current}
                    open={Boolean(saveMenuAnchor)}
                    onClose={() => setSaveMenuAnchor(null)}
                >
                    <MenuItem onClick={() => {
                        setSaveMenuAnchor(null)
                        void handleConfirm(true)
                    }}>
                        {t('Save & send email')}
                    </MenuItem>
                </Menu>
            </DialogActions>

        </Dialog>
    )
}

export default ConfirmReservationUpdateDialog
